/**
 * Dashboard Header Component
 * Shared header for all dashboard pages with search, notifications, and user profile
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, User, Settings, LogOut, X } from 'lucide-react';

export interface SearchConfig {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
    onSubmit?: () => void;
}

interface DashboardHeaderProps {
    title: string;
    subtitle?: string;
    titleColor?: 'blue' | 'black';
    searchConfig?: SearchConfig;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ title, subtitle, titleColor = 'black', searchConfig }) => {
    const navigate = useNavigate();
    const [userName, setUserName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const mobileDropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
            if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
                setIsMobileDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        navigate('/auth/login');
    };

    // Get user info from localStorage
    useEffect(() => {
        const userDataString = localStorage.getItem('authUser');
        if (userDataString) {
            try {
                const userData = JSON.parse(userDataString);
                const firstName = userData.firstName || '';
                const lastName = userData.lastName || '';
                setUserName(`${firstName} ${lastName}`.trim() || 'User');
                setUserEmail(userData.email || '');
            } catch (error) {
                console.error('Failed to parse user data:', error);
            }
        }
    }, []);

    return (
        <div className="px-4 lg:px-8 pt-2 pb-2 lg:pt-6 lg:pb-2 bg-transparent transition-all duration-300">
            {/* Mobile: Icons row aligned with hamburger (which is at left-4 top-4) */}
            <div className="flex lg:hidden items-center justify-end gap-3 mb-3">
                {/* Notification Icon - Mobile */}
                <button className="relative w-10 h-10 flex items-center justify-center bg-white border border-gray-200/80 rounded-xl hover:bg-gray-50 shadow-sm transition-all duration-200 group">
                    <Bell
                        size={18}
                        className="text-gray-600 group-hover:text-[#015FA6] transition-colors duration-300"
                    />
                    <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-[#FF4D4D] rounded-full ring-2 ring-white animate-pulse"></span>
                </button>

                {/* User Avatar - Mobile */}
                <div className="relative" ref={mobileDropdownRef}>
                    <button
                        onClick={() => setIsMobileDropdownOpen(!isMobileDropdownOpen)}
                        className="w-10 h-10 rounded-full bg-gradient-to-br from-[#015FA6] to-[#6EABFF] flex items-center justify-center shadow-md"
                    >
                        <span className="text-white font-bold text-sm font-['Manrope']">
                            {userName.charAt(0).toUpperCase()}
                        </span>
                    </button>

                    {/* Mobile Dropdown Menu */}
                    {isMobileDropdownOpen && (
                        <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200/80 py-2 z-50 animate-fade-in">
                            <button
                                onClick={() => {
                                    navigate('/candidate/my-profile');
                                    setIsMobileDropdownOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B111E] hover:bg-[#F4F7FE] transition-colors font-['Manrope']"
                            >
                                <User size={18} className="text-[#015FA6]" />
                                My Profile
                            </button>
                            <button
                                onClick={() => {
                                    navigate('/candidate/account-settings');
                                    setIsMobileDropdownOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B111E] hover:bg-[#F4F7FE] transition-colors font-['Manrope']"
                            >
                                <Settings size={18} className="text-[#015FA6]" />
                                Account Settings
                            </button>
                            <div className="my-2 border-t border-gray-100"></div>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-['Manrope']"
                            >
                                <LogOut size={18} />
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Title section */}
            <div className="flex items-center justify-between gap-4 lg:gap-6">
                <div className="flex-1 min-w-0 animate-fade-in">
                    <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold leading-tight tracking-tight truncate font-['Manrope'] ${titleColor === 'blue' ? 'text-[#015FA6]' : 'text-[#0B111E]'}`}>
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="hidden md:block text-base font-medium text-[#515151]/80 mt-1 truncate font-['Manrope']">
                            {subtitle}
                        </p>
                    )}
                </div>

                {/* Desktop: Icons inline with title */}
                <div className="hidden lg:flex items-center gap-4 xl:gap-6 flex-shrink-0">
                    {/* Search Bar - Desktop only, shown when searchConfig is provided */}
                    {searchConfig && (
                        <div className="relative group">
                            <input
                                type="text"
                                placeholder={searchConfig.placeholder}
                                value={searchConfig.value}
                                onChange={(e) => searchConfig.onChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchConfig.onSubmit) {
                                        searchConfig.onSubmit();
                                    }
                                }}
                                className="w-[240px] xl:w-[320px] h-11 pl-11 pr-9 rounded-xl bg-white border border-gray-200/80 focus:outline-none focus:border-[#015FA6] focus:ring-4 focus:ring-[#015FA6]/5 text-sm font-medium transition-all duration-300 shadow-sm hover:shadow-md font-['Manrope']"
                            />
                            <Search
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-[#015FA6] transition-colors duration-300"
                                size={18}
                            />
                            {searchConfig.value && (
                                <button
                                    onClick={() => searchConfig.onChange('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                                    aria-label="Clear search"
                                >
                                    <X size={12} className="text-gray-500" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Notification Icon - Desktop */}
                    <button className="relative w-11 h-11 flex items-center justify-center bg-white border border-gray-200/80 rounded-xl hover:bg-gray-50 hover:border-[#015FA6]/30 hover:shadow-md transition-all duration-300 group">
                        <Bell
                            size={20}
                            className="text-gray-600 group-hover:text-[#015FA6] transition-colors duration-300"
                        />
                        <span className="absolute top-3 right-3 w-2 h-2 bg-[#FF4D4D] rounded-full ring-2 ring-white animate-pulse"></span>
                    </button>

                    {/* User Profile - Desktop */}
                    <div className="flex items-center gap-3 pl-2 border-l border-gray-200/60 relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="flex items-center gap-3 group p-1.5 pr-3 rounded-xl hover:bg-white/80 hover:shadow-sm transition-all duration-300 border border-transparent hover:border-gray-100"
                        >
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#015FA6] to-[#6EABFF] flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform duration-300">
                                <span className="text-white font-bold text-sm font-['Manrope']">
                                    {userName.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="text-left">
                                <p className="text-sm font-bold text-[#0B111E] truncate max-w-[120px] font-['Manrope'] group-hover:text-[#015FA6] transition-colors">
                                    {userName}
                                </p>
                                <p className="text-xs font-medium text-[#757575] truncate max-w-[120px] font-['Manrope']">
                                    {userEmail}
                                </p>
                            </div>
                            <ChevronDown size={16} className={`text-gray-400 group-hover:text-[#015FA6] transition-all duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                            <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200/80 py-2 z-50 animate-fade-in">
                                <button
                                    onClick={() => {
                                        navigate('/candidate/my-profile');
                                        setIsDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B111E] hover:bg-[#F4F7FE] transition-colors font-['Manrope']"
                                >
                                    <User size={18} className="text-[#015FA6]" />
                                    My Profile
                                </button>
                                <button
                                    onClick={() => {
                                        navigate('/candidate/account-settings');
                                        setIsDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#0B111E] hover:bg-[#F4F7FE] transition-colors font-['Manrope']"
                                >
                                    <Settings size={18} className="text-[#015FA6]" />
                                    Account Settings
                                </button>
                                <div className="my-2 border-t border-gray-100"></div>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-['Manrope']"
                                >
                                    <LogOut size={18} />
                                    Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHeader;
